/**
 * AddMaterialModal
 *
 * Single canonical entry point for adding a new material to the workspace.
 * On save the material is written to the workspace pricebook AND a fresh
 * EstimateItem is handed back to the caller via `onAdded`. The caller decides
 * where to drop that item — into the active estimate's zone (Library path)
 * or into the private template being edited (PrivateTemplateEditor path).
 *
 * Why this exists: previously the only path to add a new material inline was
 * the "Custom Item" quick-add, which created an item-only entry that never
 * persisted to the pricebook — every estimate that needed that material
 * required retyping. This modal closes the gap by making "save to pricebook"
 * the default, non-skippable behavior.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useIdioma } from '../../i18n/idioma';
import toast from 'react-hot-toast';
import { X, Plus, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { useEstimates } from '../../contexts/EstimatesContext';
import { useSettings } from '../../contexts/SettingsContext';
import { getIndustryConfig } from '../../data/industryConfig';
import { EstimateItem, PricebookItem } from '../../types';
import { priceLineByType } from '../../utils/lineItemPricing';

export interface AddMaterialModalProps {
    isOpen: boolean;
    /** Workspace target margin percent (0..99). Used as a fallback to derive
     *  rate from cost when no Default Material Markup is configured. */
    targetMarginPercent: number;
    /** Workspace Default Material Markup percent. When > 0, this is the
     *  preferred basis for the rate (settings → Pricing & Margins). */
    materialMarkupPercent?: number;
    /** Fires after the material is written to pricebook. The caller decides
     *  what to do with the EstimateItem (drop into estimate, into template, etc.).
     *  The pricebook write has already happened by the time this is called. */
    onAdded: (item: EstimateItem, pricebookItem: { id: string; name: string; cost: number; unit: string }) => void;
    onClose: () => void;
    /** Optional pre-fill — useful if invoked from a $0 row that already has
     *  a description + unit we want to keep. */
    initial?: { name?: string; unit?: string };
}

const UNIT_SUGGESTIONS = ['EA', 'SF', 'LF', 'CY', 'TON', 'BAG', 'ROLL', 'BUNDLE', 'HR', 'DAY'];

const AddMaterialModal: React.FC<AddMaterialModalProps> = ({ isOpen, targetMarginPercent, materialMarkupPercent, onAdded, onClose, initial }) => {
    const { t } = useIdioma();
    /* El markup que se aplica al costo cuando el contratista no teclea uno.
     *
     * Es el dial de Material del taller, y punto. ANTES había un respaldo que
     * derivaba el markup del margen objetivo —`margen / (100 - margen)`— y con
     * el margen en 30% eso da 42,857%. Ese número no solo se pintaba en la
     * insignia de la fila: se guardaba en la lista de precios del taller
     * (línea ~117), y desde ahí le ganaba al dial en todos los presupuestos
     * futuros. Así se contaminaron 111 de 657 filas del pricebook.
     *
     * Un dial en 0 significa "no marcar", no "invéntate un número". */
    const defaultMarkup = Math.max(materialMarkupPercent ?? 0, 0);
    const { addPricebookItem, pricebook } = useEstimates();
    const { settings } = useSettings();
    const industry = getIndustryConfig(settings.profile.industryType);
    const categoryOptions = industry.pricebookCategories;

    const [name, setName] = useState(initial?.name ?? '');
    const [cost, setCost] = useState<string>('');
    const [unit, setUnit] = useState<string>(initial?.unit ?? 'EA');
    const [category, setCategory] = useState<string>(categoryOptions[0] ?? 'Other');
    const [markupOverride, setMarkupOverride] = useState<string>('');
    const [vendor, setVendor] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [nameError, setNameError] = useState<string | null>(null);

    // Reset form when reopening the modal so a previous session doesn't bleed in.
    useEffect(() => {
        if (isOpen) {
            setName(initial?.name ?? '');
            setCost('');
            setUnit(initial?.unit ?? 'EA');
            setCategory(categoryOptions[0] ?? 'Other');
            setMarkupOverride('');
            setVendor('');
            setNotes('');
            setNameError(null);
            setSaving(false);
        }
    }, [isOpen, initial?.name, initial?.unit, categoryOptions]);

    // Detect an obvious duplicate by exact (case-insensitive) name + unit.
    // Surface as a warning, not a block — contractors sometimes legitimately
    // carry the same material at different vendor/price points.
    const duplicateWarning = useMemo(() => {
        if (!name.trim()) return null;
        const n = name.trim().toLowerCase();
        const u = unit.trim().toLowerCase();
        const dup = pricebook.find(p =>
            p.name.toLowerCase() === n && (p.unit || '').toLowerCase() === u,
        );
        return dup ? `${t('materiales.duplicado.inicio')}${dup.name}" (${dup.unit}) — $${(dup.cost ?? 0).toFixed(2)} ${t('materiales.duplicado.fin')}` : null;
    }, [name, unit, pricebook]);

    const handleSave = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setNameError(t('comun.nombreObligatorio'));
            return;
        }
        setNameError(null);
        setSaving(true);

        const costN = parseFloat(cost) || 0;
        const overrideMarkupN = markupOverride ? parseFloat(markupOverride) : NaN;

        /* EL MARKUP QUE SE GUARDA EN LA LISTA DE PRECIOS ≠ EL QUE SE COBRA HOY.
         *
         * Antes, dejar el campo en blanco guardaba una COPIA del dial de hoy
         * (`finalMarkup = override ?? defaultMarkup`). En cuanto alguien movía
         * el dial de Material, ese 20 congelado quedaba indistinguible de un 20
         * tecleado a propósito, y le ganaba al dial para siempre: la fila seguía
         * cobrando al valor viejo mientras el taller creía haber subido precios.
         * Es el mismo mecanismo que contaminó 110 filas del pricebook.
         *
         * Ahora el campo en blanco guarda `null`, que significa «sigue el dial
         * del taller» —y NO es lo mismo que 0, que significa «se cobra a costo»
         * (`PricebookItem.markupPercent` es `number | null` justo para poder
         * distinguirlos). El marcador del campo ya promete "auto (20%)": esto es
         * lo que hace que la promesa siga siendo cierta el mes que viene.
         *
         * Un número TECLEADO sí se guarda tal cual: es una decisión del
         * contratista sobre ESE material, no un eco del dial.
         *
         * (La columna `markup_source` de la migración de la ola 1-D todavía no
         * está aplicada en producción, así que aquí solo va el nulo.) */
        const markupTecleado = Number.isFinite(overrideMarkupN) ? overrideMarkupN : null;
        /** El markup con el que se cotiza la partida de HOY: el tecleado, o el dial. */
        const markupVigente = markupTecleado ?? defaultMarkup;

        const pricebookData: Omit<PricebookItem, 'id' | 'lastUpdated'> = {
            name: trimmedName,
            cost: costN,
            unit: unit.trim() || 'EA',
            category,
            markupPercent: markupTecleado,
            // Procedencia: un número tecleado es una DECISIÓN y gana sobre el dial;
            // el campo en blanco es 'dial' y el lector relee el dial vivo.
            markupSource: (markupTecleado) === null || (markupTecleado) === undefined ? 'dial' as const : 'user' as const,
            vendor: vendor.trim() || '',
            source: 'user',
            // B-3: persist the note (was collected but silently dropped). undefined
            // when blank so we don't write an empty string.
            notes: notes.trim() || undefined,
        };

        try {
            const newId = await addPricebookItem(pricebookData);

            /* El precio, con la única regla de material que hay (markup sobre
             * costo). Dos arreglos respecto de la versión anterior:
             *   · exigía `finalMarkup > 0` para calcular la tarifa, así que un
             *     markup de 0 dejaba la fila en $0,00 en vez de a costo;
             *   · cuando la tarifa salía 0 escribía `marginPercent =
             *     targetMarginPercent`, un margen de un precio que no existía. */
            const { rate: finalRate, markupPercent, marginPercent } = priceLineByType('Material', costN, {
                targetMarginPercent,
                materialMarkupPercent: markupVigente,
            });

            const estimateItem: EstimateItem = {
                id: Math.random().toString(36).substr(2, 9),
                type: 'Material',
                description: pricebookData.name,
                quantity: 1,
                cost: costN,
                markupPercent,
                marginPercent,
                rate: finalRate,
                // El precio de la regla ES el precio base (k = 1). Sin él, esta
                // partida entraría al estimado sin línea base y el solver de
                // margen neto tendría que adivinarla (Parte A.6.6).
                baseRate: finalRate,
                total: finalRate,
                unit: pricebookData.unit,
                sourcePricebookId: newId,
                isOverridden: false,
                category: pricebookData.category,
            };

            toast.success(`${t('materiales.toast.agregado_inicio')}${pricebookData.name}" — $${costN.toFixed(2)}/${pricebookData.unit} ${t('materiales.toast.agregado_fin')}`);
            onAdded(estimateItem, { id: newId, name: pricebookData.name, cost: costN, unit: pricebookData.unit });
            onClose();
        } catch (err: any) {
            toast.error(`${t('materiales.toast.fallo')}${err?.message || t('materiales.toast.error_desconocido')}`);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-navy-950/50 animate-in fade-in" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="bg-white rounded-3xl shadow-s3 w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95">
                {/* Header */}
                <div className="px-6 py-5 border-b border-acero-200 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 grid place-items-center bg-acero-100 text-acero-600 rounded-xl shrink-0">
                            <Plus size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="font-semibold tracking-tight text-navy-950 text-lg leading-tight">{t('materiales.modal.titulo')}</h2>
                            <p className="text-xs text-acero-500 mt-1 leading-relaxed">
                                {t('materiales.modal.subtitulo')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-peligro hover:bg-peligro-bg rounded-lg transition-colors duration-150 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        aria-label={t('materiales.modal.cerrar')}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-6 py-5 space-y-4">
                    {/* Name */}
                    <div>
                        <label className="label-ui mb-1.5 block">
                            {t('materiales.campo.nombre')}
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(null); }}
                            placeholder={t('materiales.campo.nombre_placeholder')}
                            autoFocus
                            aria-invalid={nameError ? true : undefined}
                            className={`w-full h-10 px-3 bg-white border rounded-xl text-sm text-navy-950 placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150 focus:outline-none ${nameError ? 'border-peligro focus:border-peligro focus:ring-2 focus:ring-peligro/25' : 'border-acero-300 focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55'}`}
                        />
                        {nameError && (
                            <p className="text-xs text-peligro mt-1.5 flex items-center gap-1">
                                <AlertCircle size={12} /> {nameError}
                            </p>
                        )}
                        {duplicateWarning && (
                            <p className="text-xs text-aviso mt-1.5 flex items-start gap-1">
                                <AlertCircle size={12} className="mt-0.5 shrink-0" /> {duplicateWarning}
                            </p>
                        )}
                    </div>

                    {/* Cost + Unit */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-ui mb-1.5 block">
                                {t('materiales.campo.costo')}
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-acero-400 text-sm pointer-events-none">$</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={cost}
                                    onChange={(e) => setCost(e.target.value)}
                                    placeholder="0.00"
                                    className="w-full h-10 pl-7 pr-3 bg-white border border-acero-300 rounded-xl text-sm font-data tabular-nums text-navy-950 text-right placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="label-ui mb-1.5 block">
{t('materiales.campo.unidad')}
                            </label>
                            <input
                                type="text"
                                list="unit-suggestions"
                                value={unit}
                                onChange={(e) => setUnit(e.target.value.toUpperCase())}
                                placeholder="EA"
                                className="w-full h-10 px-3 bg-white border border-acero-300 rounded-xl text-sm text-navy-950 text-center uppercase placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                            />
                            <datalist id="unit-suggestions">
                                {UNIT_SUGGESTIONS.map(u => <option key={u} value={u} />)}
                            </datalist>
                        </div>
                    </div>

                    {/* Category */}
                    <div>
                        <label className="label-ui mb-1.5 block">
{t('materiales.campo.categoria')}
                        </label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full h-10 px-3 bg-white border border-acero-300 rounded-xl text-sm text-navy-950 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                        >
                            {categoryOptions.map(c => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>

                    {/* Optional: Markup override + Vendor */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-ui mb-1.5 block">
                                {t('comun.markupPct')}
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={markupOverride}
                                    onChange={(e) => setMarkupOverride(e.target.value)}
                                    placeholder={`auto (${defaultMarkup.toFixed(0)}${t('materiales.campo.markup_placeholder')}`}
                                    className="w-full h-10 pr-7 px-3 bg-white border border-acero-300 rounded-xl text-sm font-data tabular-nums text-navy-950 text-right placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-acero-400 text-sm pointer-events-none">%</span>
                            </div>
                        </div>
                        <div>
                            <label className="label-ui mb-1.5 block">
{t('materiales.campo.proveedor')}
                            </label>
                            <input
                                type="text"
                                value={vendor}
                                onChange={(e) => setVendor(e.target.value)}
                                placeholder={t('materiales.campo.opcional')}
                                className="w-full h-10 px-3 bg-white border border-acero-300 rounded-xl text-sm text-navy-950 placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                            />
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="label-ui mb-1.5 block">
{t('materiales.campo.notas')}
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                            placeholder={t('materiales.campo.notas_placeholder')}
                            rows={2}
                            className="w-full px-3 py-2.5 bg-white border border-acero-300 rounded-xl text-sm text-navy-950 placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55 resize-none"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-acero-200 flex items-center justify-between gap-3 bg-acero-50">
                    <Button variant="ghost" onClick={onClose} disabled={saving}>{t('materiales.boton.cancelar')}</Button>
                    <Button
                        variant="secondary"
                        onClick={handleSave}
                        isLoading={saving}
                        disabled={saving || !name.trim()}
                    >
                        {saving ? t('materiales.boton.guardando') : t('materiales.boton.guardar')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AddMaterialModal;
